import { useState } from "react";
import { useShallow } from "zustand/react/shallow";
import HazardIdentification from "../components/riskManager/HazardIdentification";
import RiskAnalysis from "../components/riskManager/RiskAnalysis";
import RiskControl from "../components/riskManager/RiskControl";
import RiskReport from "../components/riskManager/RiskReport";
import ContentTitle from "../layout/ContentTitle";
import { useRiskManagerStore } from "../store/useRiskManagerStore";

const RiskManager = () => {
  const [activeTab, setActiveTab] = useState("hazard");
  const visitedTabs = useRiskManagerStore(
    useShallow((state) => state.visitedTabs),
  );
  const setVisitedTabs = useRiskManagerStore((state) => state.setVisitedTabs);

  const tabs = [
    {
      id: "hazard",
      label: "Hazard Identification",
      tooltip: "Identify potential hazards associated with the device",
      content: (props) => (
        <HazardIdentification {...props} goToNextTab={goToNextTab} />
      ),
    },
    {
      id: "analysis",
      label: "Risk analysis",
      tooltip: "Analyze risks based on identified hazards",
      content: (props) => (
        <RiskAnalysis {...props} enableNextTab={enableNextTab} />
      ),
    },
    {
      id: "control",
      label: "Risk control",
      tooltip: "Define and evaluate risk control measures",
      content: (props) => (
        <RiskControl {...props} enableNextTab={enableNextTab} />
      ),
    },
    {
      id: "report",
      label: "Risk report",
      tooltip: "Generate the final risk management report",
      content: (props) => <RiskReport {...props} />,
    },
  ];

  function updateVisitedTabs(tabId) {
    let newVisitedTabs = visitedTabs.includes(tabId)
      ? visitedTabs
      : [...visitedTabs, tabId];
    setVisitedTabs(newVisitedTabs);
  }

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    updateVisitedTabs(tabId);
  };

  const goToNextTab = () => {
    const currentIdx = tabs.findIndex((tab) => tab.id === activeTab);
    if (currentIdx < tabs.length - 1) {
      const nextTabId = tabs[currentIdx + 1].id;
      setActiveTab(nextTabId);
      setVisitedTabs(["hazard", nextTabId]);
    }
  };

  const enableNextTab = () => {
    const currentIdx = tabs.findIndex((tab) => tab.id === activeTab);
    if (currentIdx < tabs.length - 1) {
      const nextTabId = tabs[currentIdx + 1].id;
      updateVisitedTabs(nextTabId);
    }
  };

  return (
    <div
      className="flex h-full w-full flex-col overflow-y-hidden"
      style={{ minHeight: "calc(100vh - 60px)" }}
    >
      <ContentTitle title={"Risk Manager"} />

      <div className="tabs tabs-boxed mt-4 ml-3">
        {tabs.map((tab, idx) => {
          const isVisited = visitedTabs.includes(tab.id);
          return (
            <div className="tooltip tooltip-center" data-tip={tab.tooltip}>
              <button
                key={tab.id}
                className={`
                  tab relative mb-1 -ml-2 overflow-visible pl-6
                  text-sm md:text-base tracking-wide
                  transition-all duration-300 ease-in-out
                  ${activeTab === tab.id
                    ? `
                        tab-active
                        bg-primary/70
                        scale-105
                        font-bold
                        border border-gray-300
                        text-white
                        shadow-md
                        hover:text-white
                        dark:border-gray-600
                        dark:bg-gray-700
                        dark:text-white
                      `
                    : isVisited
                      ? `
                          font-medium
                          text-base-content
                          opacity-90
                          border border-transparent
                          bg-base-200
                          hover:opacity-100
                          dark:bg-base-300
                        `
                      : `
                          cursor-not-allowed
                          font-medium
                          text-base-content
                          opacity-60
                          border border-transparent
                          bg-base-200
                          hover:opacity-100
                          dark:bg-base-300
                        `
                  }
                `}
                style={{
                  clipPath:
                    idx === 0
                      ? "polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%)"
                      : "polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%, 30px 0, 30px 100%)",
                }}
                onClick={() => isVisited && handleTabClick(tab.id)}
                disabled={!isVisited}
              >
                {tab.label}
              </button>
            </div>
          );
        })}
      </div>
      <div className="card bg-base-100 mt-1 flex flex-1 flex-col overflow-hidden p-1 shadow-xl">
        {tabs.find((tab) => tab.id === activeTab)?.content({ goToNextTab })}
      </div>
    </div>
  );
};
export default RiskManager;
